<style lang="scss">
  @import '../libs/tooltip/tooltip.css';

  $background-color: #009EE2;
  $border-color: #2d86ad;

  .help-icon {
    display: inline-block;
    vertical-align: middle;
    height: 29px;
    width: 29px;
    margin-left: 0.5rem;
    cursor: help;
    position: absolute;

    // TODO: Change these colours!
    &.warning {
      &:hover, &.active { .icon-background-fill { fill: darken(#ddc12e, 10%); } }
      .icon-background-fill { fill: #ddc12e; }
    }

    &.success {
      &:hover, &.active { .icon-background-fill { fill: darken(#8dc572, 20%); } }
      .icon-background-fill { fill: #8dc572; }
    }

    &.error {
      &:hover, &.active { .icon-background-fill { fill: darken(#be6464, 20%); } }
      .icon-background-fill { fill: #be6464; }
    }

    &:hover, &.active { .icon-background-fill { fill: #575756; } }
    .icon-background-fill {
      fill: #DADADA;
      transition: 250ms fill;
    }
    .icon-fill { fill: #FFFFFF; }
  }

  .tooltip {
    z-index: 10;
    padding: 1rem;
    border-radius: 8px;
    background: $background-color;
    transition: 250ms background, 250ms opacity, 250ms transform !important;
    -webkit-touch-callout: auto;
    -webkit-user-select: auto;
    user-select: auto;
    pointer-events: auto;
    border: 1px solid $border-color;

    &.success {
      border-color: #5bb530;

      &:after {
        border-color: #5bb530;
      }
    }
    &.error {
      border-color: #a23838;

      &:after {
        border-color: #a23838;
      }
    }
    &.warning {
      border-color: #b59b10;

      &:after {
        border-color: #b59b10;
      }
    }

    &:after {
      border-color: $border-color;
    }

    @media screen and (max-width: 39.9375em) {
      /* !important to overcome inline css from plugin */
      top: 0.625rem;
      left: 0.625rem !important;
      right: 0.625rem !important;
      max-width: 100%;
    }

    p {
      margin-bottom: 0;
      &.additional { margin-top: 0.5rem; }
    }
  }
</style>

<template>
  <span :id="id" class="help-icon" :class="{ active: clicked, success: isSuccess, warning: isWarning, error: isError }" v-html="icon"></span>
</template>

<script>
import _ from 'lodash';
import '@/libs/tooltip/tooltip';

export default {
  props: {
    message: {
      type: String,
      default: '',
    },
    isError: {
      type: Boolean,
    },
    errorMessage: {
      type: String,
    },
    isSuccess: {
      type: Boolean,
    },
    successMessage: {
      type: String,
    },
    isWarning: {
      type: Boolean,
    },
    warningMessage: {
      type: String,
    },
    htmlMode: {
      type: Boolean,
      default: false,
    },
    effect: {
      type: String,
      default: 'slide',
    },
    place: {
      type: String,
      default: 'top-right',
    },
  },
  data() {
    return {
      id: _.uniqueId('tooltip_'),
      icon: require('!html-loader!@/assets/help.svg'),
      tooltip: {},
      element: {},
      clicked: false,
      visible: false,
    };
  },
  watch: {
    message() {
      this.updateTooltip();
    },
    successMessage() {
      this.updateTooltip();
    },
    isSuccess() {
      this.updateTooltip();
    },
    errorMessage() {
      this.updateTooltip();
    },
    isError() {
      this.updateTooltip();
    },
    warningMessage() {
      this.updateTooltip();
    },
    isWarning() {
      this.updateTooltip();
    },
  },
  methods: {
    showClickedTooltip() {
      this.clicked = !this.clicked;
      if (this.clicked) this.showTooltip();
      else this.hideTooltip();
    },
    showMouseOverTooltip() {
      this.showTooltip();
    },
    showTooltip() {
      this.updateTooltip();
      this.visible = true;
      this.tooltip.show();
    },
    hideMouseOverTooltip() {
      if (!this.clicked) this.hideTooltip();
    },
    hideTooltip() {
      this.visible = false;
      this.clicked = false;
      this.tooltip.hide();
    },
    hideTooltipIfVisible() {
      if (this.visible) {
        this.hideTooltip();
      }
    },
    updateTooltip() {
      let message = '';
      if (this.isError && this.errorMessage) message = `${this.errorMessage}`;
      else if (this.isWarning && this.warningMessage) message = `${this.warningMessage}`;
      else if (this.isSuccess && this.successMessage) message = `${this.successMessage}`;

      // check to see if medium-up is in dom
      const isSmallDevice = document.querySelector('.show-for-medium').offsetParent === null;
      this.tooltip.place(isSmallDevice ? 'top-right' : this.place);
      this.tooltip.type(this.tooltipClass);
      if (this.htmlMode) {
        this.tooltip.content(`${this.message}${message}`);
      } else {
        let html = `<p>${this.message}</p>`;
        if (message) html += `<p class="additional">${message}</p>`;
        this.tooltip.content(html);
      }
    },
    addEventListeners() {
      document.body.addEventListener('click', this.hideTooltipIfVisible, true);
      this.element.addEventListener('click', this.showClickedTooltip, true);
      this.element.addEventListener('mouseover', this.showMouseOverTooltip, true);
      this.element.addEventListener('mouseout', this.hideMouseOverTooltip, true);
    },
    removeEventListeners() {
      document.body.removeEventListener('click', this.hideTooltipIfVisible, true);
      this.element.removeEventListener('click', this.showClickedTooltip, true);
      this.element.removeEventListener('mouseover', this.showMouseOverTooltip, true);
      this.element.removeEventListener('mouseout', this.hideMouseOverTooltip, true);
    },
  },
  computed: {
    tooltipClass() {
      let tooltip = 'info';
      if (this.isError) tooltip = 'error';
      else if (this.isWarning) tooltip = 'warning';
      else if (this.isSuccess) tooltip = 'success';

      return tooltip;
    },
  },
  mounted() {
    // API: https://github.com/darsain/tooltip/wiki/Tooltip
    this.element = document.getElementById(this.id);
    this.tooltip = new window.Tooltip(this.message);
    this.tooltip.attach(this.element);
    this.tooltip.effect(this.effect);
    this.updateTooltip();
    this.addEventListeners();
  },
  beforeDestroy() {
    this.tooltip.detach();
    this.removeEventListeners();
  },
};
</script>
